<template>
  <VatValidationView
    mode="address"
    :address-nr="currentAddressNr"
    :embedded="isEmbedded"
    :customer="customer"
    :tenant="tenant"
  />
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import {
  vatValidationApi,
  type AddressStatusSummary,
  type AuditProtocol,
  type ValidationHistoryItem,
} from '@/api/vatValidation'
import VatValidationView from '@/components/vat/VatValidationView.vue'
import { getMatchMeaning } from '@/utils/vatCodes'

const props = defineProps({
  addressNr: { type: String, default: '' },
  embedded: { type: Boolean, default: false },
  customer: { type: String, default: '' },
  tenant: { type: String, default: '' },
})

const route = useRoute()
const authStore = getActivePinia() ? useAuthStore() : null
const toast = useToast()

const currentAddressNr = computed(() => {
  return props.addressNr || (route.params.addressNr as string) || ''
})

const isEmbedded = computed(() => {
  return props.embedded || route.query.embedded === 'true' || Boolean(route.meta.embedded)
})

const tenant = computed(
  () =>
    props.tenant ||
    (route.query.tenant as string) ||
    authStore?.userInfo?.activeTenant?.tenantName ||
    undefined,
)
const customer = computed(
  () =>
    props.customer ||
    (route.query.customer as string) ||
    authStore?.userInfo?.activeTenant?.customerNo ||
    undefined,
)

const loading = ref(true)
const validating = ref(false)
const fetchError = ref('')
const statusSummary = ref<AddressStatusSummary | null>(null)
const latestProtocol = ref<AuditProtocol | null>(null)

const selectedProtocol = ref<AuditProtocol | null>(null)
const isProtocolModalOpen = ref(false)

const isCompletelyCorrect = computed(() => {
  if (!statusSummary.value) return false
  if (statusSummary.value.status !== 'OFFICIALLY_CONFIRMED') return false
  if (latestProtocol.value?.result) {
    const { companyNameMatch, streetMatch, zipMatch, cityMatch } = latestProtocol.value.result
    return companyNameMatch === 'A' && streetMatch === 'A' && zipMatch === 'A' && cityMatch === 'A'
  }
  return statusSummary.value.valid
})

const effectiveStatus = computed(() => {
  if (!statusSummary.value) return ''
  const status = statusSummary.value.status
  if (status === 'OFFICIALLY_CONFIRMED') {
    return isCompletelyCorrect.value ? 'OFFICIALLY_CONFIRMED' : 'PARTIALLY_CONFIRMED'
  }
  if (status === 'OFFICIALLY_NOT_CONFIRMED') {
    if (latestProtocol.value?.result) {
      const { companyNameMatch, streetMatch, zipMatch, cityMatch } = latestProtocol.value.result
      const countA = [companyNameMatch, streetMatch, zipMatch, cityMatch].filter(
        (m) => m === 'A',
      ).length
      if (countA > 0) {
        return 'PARTIALLY_CONFIRMED'
      }
    } else if (statusSummary.value.history && statusSummary.value.history.length > 0) {
      const latest = statusSummary.value.history[0]
      const countA = [
        latest.companyNameMatch,
        latest.streetMatch,
        latest.zipMatch,
        latest.cityMatch,
      ].filter((m) => m === 'A').length
      if (countA > 0) {
        return 'PARTIALLY_CONFIRMED'
      }
    }
  }
  return status
})

const effectiveStatusDisplay = computed(() => {
  if (!statusSummary.value) return ''
  const eff = effectiveStatus.value
  if (eff === 'OFFICIALLY_CONFIRMED') return 'Ausland: Gültig'
  if (eff === 'PARTIALLY_CONFIRMED') return 'Ausland: Eingeschränkt gültig'
  if (eff === 'OFFICIALLY_NOT_CONFIRMED') return 'Ausland: Ungültig'
  if (eff === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || eff === 'FORMALLY_VALID') return 'Inland: Gültig'
  if (eff === 'FORMALLY_INVALID' || eff === 'FORMAT_ERROR') {
    if (!statusSummary.value.testedVatId || statusSummary.value.status === 'MISSING_VAT_ID') {
      return 'Keine USt-IdNr.'
    }
    return 'Inland: Ungültig'
  }
  if (eff === 'MISSING_VAT_ID') return 'Keine USt-IdNr.'
  return statusSummary.value.statusDisplay || eff
})

const statusBadgeColor = computed(() => {
  const status = effectiveStatus.value
  switch (status) {
    case 'OFFICIALLY_CONFIRMED':
      return 'success'
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'CHECK_NOT_POSSIBLE':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
    case 'TECHNICAL_ERROR':
      return 'error'
    default:
      return 'neutral'
  }
})

function getHistoryItemDisplay(item: ValidationHistoryItem): string {
  const isAllA =
    item.allMatchesCodeA === true ||
    (item.companyNameMatch === 'A' &&
      item.streetMatch === 'A' &&
      item.zipMatch === 'A' &&
      item.cityMatch === 'A')
  const hasMatches = Boolean(
    item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
  )

  if (isAllA) {
    return 'Ausland: Gültig'
  }

  if (hasMatches) {
    return 'Ausland: Eingeschränkt gültig'
  }

  if (item.status === 'OFFICIALLY_CONFIRMED') {
    return item.statusDisplay || 'Ausland: Gültig'
  }
  if (item.status === 'PARTIALLY_CONFIRMED') {
    return 'Ausland: Eingeschränkt gültig'
  }
  if (item.status === 'OFFICIALLY_NOT_CONFIRMED') {
    return 'Ausland: Ungültig'
  }
  if (item.status === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || item.status === 'FORMALLY_VALID') {
    return 'Inland: Gültig'
  }
  if (item.status === 'FORMALLY_INVALID' || item.status === 'FORMAT_ERROR') {
    if (item.officialStatusCode === 'MISSING_VAT_ID') {
      return 'Keine USt-IdNr.'
    }
    return 'Inland: Ungültig'
  }
  if (item.status === 'MISSING_VAT_ID') {
    return 'Keine USt-IdNr.'
  }
  return item.statusDisplay || item.status
}

function getHistoryItemColor(status: string, item?: ValidationHistoryItem) {
  if (item) {
    const isAllA =
      item.allMatchesCodeA === true ||
      (item.companyNameMatch === 'A' &&
        item.streetMatch === 'A' &&
        item.zipMatch === 'A' &&
        item.cityMatch === 'A')
    const hasMatches = Boolean(
      item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
    )

    if (isAllA) {
      return 'success'
    }

    if (hasMatches) {
      return 'warning'
    }
  }

  if (status === 'OFFICIALLY_CONFIRMED') {
    return 'success'
  }
  switch (status) {
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'CHECK_NOT_POSSIBLE':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
    case 'TECHNICAL_ERROR':
      return 'error'
    default:
      return 'neutral'
  }
}

function getMatchIcon(match?: string) {
  switch (match) {
    case 'A':
      return 'i-lucide-check-circle-2'
    case 'B':
      return 'i-lucide-x-circle'
    case 'C':
      return 'i-lucide-minus-circle'
    case 'D':
      return 'i-lucide-help-circle'
    default:
      return 'i-lucide-minus'
  }
}

function getMatchColor(match?: string) {
  switch (match) {
    case 'A':
      return 'text-green-500'
    case 'B':
      return 'text-red-500'
    case 'C':
      return 'text-gray-400'
    case 'D':
      return 'text-amber-500'
    default:
      return 'text-gray-400'
  }
}

function getMatchLabel(match?: string) {
  if (!match) return '-'
  return `Code ${match}: ${getMatchMeaning(match)}`
}

function hasMatchResults(item: ValidationHistoryItem): boolean {
  return Boolean(
    item.companyNameMatch ||
      item.streetMatch ||
      item.zipMatch ||
      item.cityMatch ||
      item.allMatchesCodeA !== undefined,
  )
}

function getAMatchPercentage(item: ValidationHistoryItem): number {
  if (item.allMatchesCodeA === true) {
    return 100
  }
  const fields = [item.companyNameMatch, item.streetMatch, item.zipMatch, item.cityMatch]
  const countA = fields.filter((m) => m === 'A').length
  return Math.round((countA / 4) * 100)
}

function getAMatchPercentageColor(item: ValidationHistoryItem): string {
  const pct = getAMatchPercentage(item)
  if (pct === 100) {
    return 'text-green-600 dark:text-green-400 font-bold'
  }
  if (pct > 0) {
    return 'text-amber-600 dark:text-amber-400 font-semibold'
  }
  return 'text-red-600 dark:text-red-400 font-semibold'
}

function getAMatchPercentageTitle(item: ValidationHistoryItem): string {
  const pct = getAMatchPercentage(item)
  const parts: string[] = []
  if (item.companyNameMatch) parts.push(`Firmenname: ${item.companyNameMatch}`)
  if (item.streetMatch) parts.push(`Straße: ${item.streetMatch}`)
  if (item.zipMatch) parts.push(`PLZ: ${item.zipMatch}`)
  if (item.cityMatch) parts.push(`Ort: ${item.cityMatch}`)

  if (parts.length > 0) {
    return `A-Status: ${pct}% (${parts.join(', ')})`
  }
  return `A-Status: ${pct}%`
}

function formatTimestamp(ts?: string) {
  if (!ts) return '-'
  try {
    return new Date(ts).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ts
  }
}

async function fetchData() {
  if (!currentAddressNr.value) return
  loading.value = true
  fetchError.value = ''
  try {
    const res = await vatValidationApi.getAddressStatus(
      currentAddressNr.value,
      tenant.value,
      customer.value,
    )

    // Ensure all history entries are preserved and merged (no overwrites)
    const existingHistory = statusSummary.value?.history || []
    const mergedMap = new Map<string, ValidationHistoryItem>()

    // Add fetched items from server
    for (const item of res.data.history || []) {
      const isAllA =
        item.allMatchesCodeA === true ||
        (item.companyNameMatch === 'A' &&
          item.streetMatch === 'A' &&
          item.zipMatch === 'A' &&
          item.cityMatch === 'A')
      const hasMatches = Boolean(
        item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
      )
      if (hasMatches && !isAllA) {
        item.status = 'PARTIALLY_CONFIRMED'
        item.statusDisplay = 'Ausland: Eingeschränkt gültig'
        item.allMatchesCodeA = false
      } else if (item.status === 'PARTIALLY_CONFIRMED') {
        item.statusDisplay = 'Ausland: Eingeschränkt gültig'
      } else if (item.status === 'OFFICIALLY_NOT_CONFIRMED') {
        item.statusDisplay = 'Ausland: Ungültig'
      } else if (item.status === 'OFFICIALLY_CONFIRMED') {
        item.statusDisplay = 'Ausland: Gültig'
      }

      const key = item.validationId || `${item.timestamp}-${item.trigger}`
      mergedMap.set(key, item)
    }
    // Retain any existing items that might be in-flight
    for (const item of existingHistory) {
      const key = item.validationId || `${item.timestamp}-${item.trigger}`
      if (!mergedMap.has(key)) {
        mergedMap.set(key, item)
      }
    }

    const mergedList = Array.from(mergedMap.values())
    mergedList.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
    res.data.history = mergedList

    if (res.data.history && res.data.history.length > 0) {
      const latest = res.data.history[0]
      if (
        latest.status === 'PARTIALLY_CONFIRMED' &&
        (res.data.status === 'OFFICIALLY_CONFIRMED' || res.data.status === 'OFFICIALLY_NOT_CONFIRMED')
      ) {
        res.data.status = 'PARTIALLY_CONFIRMED'
        res.data.statusDisplay = 'Ausland: Eingeschränkt gültig'
      }
    }

    statusSummary.value = res.data
    if (res.data.lastValidationId) {
      const pRes = await vatValidationApi.getAuditProtocol(
        currentAddressNr.value,
        res.data.lastValidationId,
        tenant.value,
        customer.value,
      )
      latestProtocol.value = pRes.data
    }
  } catch (err: any) {
    if (err.response?.status === 404) {
      statusSummary.value = null
    } else {
      fetchError.value = err.response?.data?.error || 'Fehler beim Laden des Prüfstatus'
    }
  } finally {
    loading.value = false
  }
}

async function triggerManualValidation() {
  validating.value = true
  try {
    const payload: Record<string, string> = {}
    if (statusSummary.value?.testedVatId) {
      payload.vatId = statusSummary.value.testedVatId
      payload.requestedVatId = statusSummary.value.testedVatId
    }
    if (route.query.vatId) {
      payload.vatId = String(route.query.vatId).trim()
      payload.requestedVatId = String(route.query.vatId).trim()
    }
    if (route.query.companyName) {
      payload.companyName = String(route.query.companyName).trim()
    }
    if (statusSummary.value?.submittedData?.companyName && !payload.companyName) {
      payload.companyName = statusSummary.value.submittedData.companyName
    }
    if (statusSummary.value?.submittedData?.street) {
      payload.street = statusSummary.value.submittedData.street
    }
    if (statusSummary.value?.submittedData?.zip) {
      payload.zip = statusSummary.value.submittedData.zip
    }
    if (statusSummary.value?.submittedData?.city) {
      payload.city = statusSummary.value.submittedData.city
    }
    if (statusSummary.value?.submittedData?.country) {
      payload.country = statusSummary.value.submittedData.country
    }
    if (statusSummary.value?.submittedData?.ownVatId) {
      payload.ownVatId = statusSummary.value.submittedData.ownVatId
    }
    const res = await vatValidationApi.validateAddress(
      currentAddressNr.value,
      payload,
      tenant.value,
      customer.value,
    )

    latestProtocol.value = res.data

    // Ensure immediate entry in history for this manual execution
    const allA =
      res.data.result?.companyNameMatch === 'A' &&
      res.data.result?.streetMatch === 'A' &&
      res.data.result?.zipMatch === 'A' &&
      res.data.result?.cityMatch === 'A'

    const hasMatches = Boolean(
      res.data.result?.companyNameMatch ||
      res.data.result?.streetMatch ||
      res.data.result?.zipMatch ||
      res.data.result?.cityMatch,
    )

    let statusToUse = res.data.status
    let statusDisplayToUse = res.data.statusDisplay
    if (hasMatches && !allA) {
      statusToUse = 'PARTIALLY_CONFIRMED'
      statusDisplayToUse = 'Ausland: Eingeschränkt gültig'
    } else if (statusToUse === 'PARTIALLY_CONFIRMED') {
      statusDisplayToUse = 'Ausland: Eingeschränkt gültig'
    } else if (statusToUse === 'OFFICIALLY_NOT_CONFIRMED') {
      statusDisplayToUse = 'Ausland: Ungültig'
    } else if (statusToUse === 'OFFICIALLY_CONFIRMED') {
      statusDisplayToUse = 'Ausland: Gültig'
    }

    const newHistoryItem: ValidationHistoryItem = {
      validationId: res.data.validationId,
      timestamp: res.data.timestamp,
      trigger: res.data.trigger,
      status: statusToUse,
      statusDisplay: statusDisplayToUse,
      valid: res.data.result?.valid ?? false,
      officialStatusCode: res.data.result?.officialStatusCode,
      companyNameMatch: res.data.result?.companyNameMatch,
      streetMatch: res.data.result?.streetMatch,
      zipMatch: res.data.result?.zipMatch,
      cityMatch: res.data.result?.cityMatch,
      allMatchesCodeA: allA,
    }

    const existingHistory = statusSummary.value?.history || []
    const updatedHistory = [
      newHistoryItem,
      ...existingHistory.filter((h) => h.validationId !== newHistoryItem.validationId),
    ]

    if (!statusSummary.value) {
      statusSummary.value = {
        customer: res.data.customer,
        tenant: res.data.tenant,
        addressNr: res.data.addressNr,
        lastCheckTimestamp: res.data.timestamp,
        lastValidationId: res.data.validationId,
        status: statusToUse,
        statusDisplay: statusDisplayToUse,
        testedVatId: res.data.submittedData?.requestedVatId || '',
        valid: res.data.result?.valid ?? false,
        snapshotHash: '',
        submittedData: res.data.submittedData,
        history: updatedHistory,
      }
    } else {
      statusSummary.value.lastCheckTimestamp = res.data.timestamp
      statusSummary.value.lastValidationId = res.data.validationId
      statusSummary.value.status = statusToUse
      statusSummary.value.statusDisplay = statusDisplayToUse
      statusSummary.value.testedVatId =
        res.data.submittedData?.requestedVatId || statusSummary.value.testedVatId
      statusSummary.value.valid = res.data.result?.valid ?? false
      statusSummary.value.submittedData = res.data.submittedData
      statusSummary.value.history = updatedHistory
    }

    toast.add({
      title: 'Prüfung erfolgreich ausgeführt',
      description: `Ergebnis: ${statusDisplayToUse}`,
      color: statusToUse === 'OFFICIALLY_CONFIRMED' ? 'success' : 'warning',
    })
    await fetchData()
  } catch (error: unknown) {
    const err = error as { response?: { data?: { details?: string; error?: string } }; message?: string }
    toast.add({
      title: 'Prüfung fehlgeschlagen',
      description: err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Prüfung fehlgeschlagen',
      color: 'error',
    })
  } finally {
    validating.value = false
  }
}

async function viewProtocol(validationId: string) {
  try {
    const res = await vatValidationApi.getAuditProtocol(
      currentAddressNr.value,
      validationId,
      tenant.value,
      customer.value,
    )
    selectedProtocol.value = res.data
    isProtocolModalOpen.value = true
  } catch (error: unknown) {
    const err = error as { message?: string }
    toast.add({
      title: 'Fehler beim Laden des Protokolls',
      description: err?.message || 'Fehler beim Laden des Protokolls',
      color: 'error',
    })
  }
}

defineExpose({
  statusSummary,
  latestProtocol,
  selectedProtocol,
  isProtocolModalOpen,
  fetchError,
  loading,
  validating,
  effectiveStatus,
  effectiveStatusDisplay,
  statusBadgeColor,
  getHistoryItemDisplay,
  getHistoryItemColor,
  getMatchIcon,
  getMatchColor,
  getMatchLabel,
  hasMatchResults,
  getAMatchPercentageColor,
  getAMatchPercentageTitle,
  formatTimestamp,
  triggerManualValidation,
  viewProtocol,
  fetchData,
})

watch(currentAddressNr, fetchData)
onMounted(fetchData)
</script>
